/**
 * Live Draft — its own full page, one of the three Draft Board sections
 * (see pages/DraftBoard.jsx, the landing/picker page this is reached
 * from). Draft Board rebuild Stage 2 — see engineering/ARCHIVE/completed-prompts/JAI_DraftBoard_Rebuild_Prompt.md.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useLeagues } from '../../context/LeagueContext'
import { draftBoardApi } from '../../services/api'
import { useDraftPoll } from '../../hooks/useDraftPoll'
import { useMinWidth } from '../../hooks/useMinWidth'
import useEspnDraftBridge from '../../hooks/useEspnDraftBridge'
import { trackEvent } from '../../analytics'
import { STAT_GLYPHS } from '../../config/statGlyphs'
import AppShell from '../../components/AppShell'
import ToolHeader from '../../components/ToolHeader'
import { JAI_FONTS } from '../../components/JaiBrand'
import { V2, V2Card, V2Emphasis, PosChip, solidChip, pctTone, Notice } from '../../components/JaiV2'
import { JAIMascot } from '../../components/JAIMascot'
import MascotDialog from '../../components/draft/MascotDialog'
import { teamColor } from '../../config/teamColors'
import DraftTicker from './DraftTicker'
import { IS_NATIVE_APP } from '../../services/runtime'
import { hapticDraftPick } from '../../services/haptics'
import { normaliseName } from '../../utils/normaliseName'

const TAG_COLOR = { sleeper: V2.blue, safe: V2.success, bust: V2.error }
// JAI's researched read on a player (news, role change, injury); words, not emoji.
const SIGNAL_LABEL = { riser: 'Rising', sleeper: 'Hidden gem', faller: 'Likely to slide' }

// ── Recommendation row (compact form of the Stage 1 player row) ──────────────

function RecommendationRow({ target, sport, onDark }) {
  const team = teamColor(target.team, sport)
  const struck = target.strikethrough
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '11px 4px',
      borderBottom: `1px solid ${onDark ? 'rgba(255,255,255,0.15)' : V2.hairSoft}`, opacity: struck ? 0.45 : 1, transition: 'opacity 200ms',
    }}>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{
          fontSize: 13.5, fontWeight: 700, color: onDark ? 'white' : V2.navy,
          textDecoration: struck ? 'line-through' : 'none',
        }}>
          {target.name}
        </span>
        {target.position && <span style={{ marginLeft: 8 }}><PosChip>{target.position}</PosChip></span>}
        {target.team && (
          <span style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 700, color: team.ink, background: team.primary, borderRadius: 999, padding: '1px 7px' }}>
            {target.team}
          </span>
        )}
        {!struck && SIGNAL_LABEL[target.signal] && (
          <span style={{ marginLeft: 6, fontSize: 9.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', ...solidChip(target.signal === 'faller' ? V2.error : V2.blue), borderRadius: 5, padding: '2px 6px' }}>
            {SIGNAL_LABEL[target.signal]}
          </span>
        )}
        {!struck && target.evidence && (
          <span style={{ display: 'block', marginTop: 3, fontSize: 11.5, lineHeight: 1.4, color: onDark ? 'rgba(255,255,255,0.75)' : V2.mute }}>
            {target.evidence}
          </span>
        )}
      </span>
      {!struck && (
        <>
          <span style={{ fontSize: 12, fontWeight: 700, color: pctTone(target.acquireProbability || 0), fontFamily: JAI_FONTS.mono, flexShrink: 0 }}>
            {Math.round((target.acquireProbability || 0) * 100)}%
          </span>
          <span style={{
            fontSize: 10, fontWeight: 800, textTransform: 'uppercase', flexShrink: 0,
            ...solidChip(TAG_COLOR[target.tag] || V2.mute),
            borderRadius: 6, padding: '2px 7px',
          }}>
            {target.tag}
          </span>
        </>
      )}
      {struck && <span style={{ fontSize: 11.5, color: V2.mute, flexShrink: 0 }}>drafted</span>}
    </div>
  )
}

// ── Recommendations panel ─────────────────────────────────────────────────

function RecommendationsPanel({ leagueId, sport, myNextPick, recommendations, onTheClock, hasStrategy = true }) {
  const navigate = useNavigate()
  const [displayed, setDisplayed] = useState([])
  // Keyed by overallPick so switching to a new pick clears the old answer
  // without an extra "loading" boolean — loading is simply "no entry yet
  // for this pick".
  const [adviceByPick, setAdviceByPick] = useState({})
  const pendingRef = useRef({})
  const adviceRequestedRef = useRef(null)

  useEffect(() => {
    setDisplayed((prev) => {
      if (!recommendations.length) return []
      if (!prev.length) return recommendations.map((r) => ({ ...r, strikethrough: false }))
      return recommendations.map((r, i) => {
        const prevItem = prev[i]
        if (r.replacedFrom && prevItem && prevItem.name === r.replacedFrom && !prevItem.strikethrough) {
          if (!pendingRef.current[i]) {
            pendingRef.current[i] = true
            setTimeout(() => {
              setDisplayed((d) => { const copy = [...d]; copy[i] = { ...r, strikethrough: false }; return copy })
              delete pendingRef.current[i]
            }, 3000)
          }
          return { ...prevItem, strikethrough: true }
        }
        if (pendingRef.current[i]) return prevItem // still holding the strike-through
        return { ...r, strikethrough: false }
      })
    })
  }, [recommendations])

  const onTheClockNow = myNextPick?.picksAway === 0

  // Fires the one Claude call for this pick, exactly once — the ref guard
  // (not React state) is what makes a second effect run for the same
  // overallPick a no-op, so there's nothing to set synchronously here.
  useEffect(() => {
    if (!onTheClockNow || !myNextPick || !recommendations.length) return
    if (adviceRequestedRef.current === myNextPick.overallPick) return
    adviceRequestedRef.current = myNextPick.overallPick
    const overallPick = myNextPick.overallPick
    draftBoardApi.getDraftAdvice(leagueId, overallPick, recommendations)
      .then((res) => setAdviceByPick((prev) => ({ ...prev, [overallPick]: res.data.advice })))
      .catch(() => setAdviceByPick((prev) => ({ ...prev, [overallPick]: '' })))
  }, [onTheClockNow, myNextPick, recommendations, leagueId])

  const advice = myNextPick ? adviceByPick[myNextPick.overallPick] : undefined
  const adviceLoading = onTheClockNow && advice === undefined

  if (!myNextPick) {
    return <V2Card><div style={{ fontSize: 13, color: V2.mute }}>No more picks left in your plan.</div></V2Card>
  }

  const header = onTheClockNow
    ? 'You\'re on the clock'
    : `Your next pick — Round ${myNextPick.round}, Pick ${myNextPick.pickInRound} (${myNextPick.picksAway} away)`

  const content = (
    <>
      <div style={{ fontSize: 14, fontWeight: 800, color: onTheClockNow ? 'white' : V2.navy, letterSpacing: '0.03em', textTransform: 'uppercase', marginBottom: onTheClock && !onTheClockNow ? 2 : 12 }}>
        {header}
      </div>
      {!onTheClockNow && onTheClock?.teamName && (
        <div style={{ fontSize: 11.5, color: V2.mute, marginBottom: 10 }}>
          On the clock now: {onTheClock.teamName}
        </div>
      )}
      <button
        type="button" data-testid="live-pick-trades-link"
        onClick={() => navigate(`/league/${leagueId}/draft/strategy?trades=1`)}
        style={{ background: 'none', border: 'none', padding: '6px 0', marginBottom: 8, color: onTheClockNow ? 'rgba(255,255,255,0.85)' : V2.blue, fontWeight: 700, fontSize: 12, cursor: 'pointer', textAlign: 'left' }}
      >
        Traded picks? Update them
      </button>
      {onTheClockNow && (
        <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.75)', lineHeight: 1.55, marginBottom: 14, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <JAIMascot size={26} state="thinking" animate={adviceLoading} />
          <span>{adviceLoading ? 'Weighing the pick…' : (advice || 'No advice available for this pick.')}</span>
        </div>
      )}
      {displayed.length === 0 && !hasStrategy && (
        <div style={{ fontSize: 12.5, color: onTheClockNow ? 'rgba(255,255,255,0.75)' : V2.mute, lineHeight: 1.55 }}>
          JAI has no plan to follow yet. Lock a strategy to get pick-by-pick recommendations here.
          <div style={{ marginTop: 8 }}>
            <button onClick={() => navigate(`/league/${leagueId}/draft/strategy`)} style={{ background: 'none', border: 'none', padding: 0, color: onTheClockNow ? 'white' : V2.blue, fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}>
              Set your strategy &rarr;
            </button>
          </div>
        </div>
      )}
      {displayed.length === 0 && hasStrategy && (
        <div style={{ fontSize: 12.5, color: onTheClockNow ? 'rgba(255,255,255,0.6)' : V2.mute }}>
          No good fit left — JAI would rather you took the best available.
        </div>
      )}
      {displayed.map((t, i) => (
        t.noFitLeft
          ? <div key={i} style={{ padding: '11px 4px', fontSize: 12.5, color: onTheClockNow ? 'rgba(255,255,255,0.6)' : V2.mute, borderBottom: `1px solid ${onTheClockNow ? 'rgba(255,255,255,0.15)' : V2.hairSoft}` }}>
              No good fit left — JAI would rather you took the best available. ({t.name} is off the board.)
            </div>
          : <RecommendationRow key={i} target={t} sport={sport} onDark={onTheClockNow} />
      ))}
    </>
  )

  return onTheClockNow ? <V2Emphasis>{content}</V2Emphasis> : <V2Card>{content}</V2Card>
}

// ── Right rail: Strategy Overview + JAI comments ──────────────────────────

function StrategyOverviewCard({ leagueId, strategySummary }) {
  const navigate = useNavigate()
  if (!strategySummary) {
    return (
      <V2Card>
        <div style={{ fontSize: 13, color: V2.mute }}>No strategy locked yet.</div>
        <button onClick={() => navigate(`/league/${leagueId}/draft/strategy`)} style={{ marginTop: 8, background: 'none', border: 'none', color: V2.blue, fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}>
          Set your strategy &rarr;
        </button>
      </V2Card>
    )
  }
  return (
    <V2Card>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: V2.faint, textTransform: 'uppercase', marginBottom: 6 }}>Strategy</div>
      <div style={{ fontSize: 14.5, fontWeight: 800, color: V2.navy, marginBottom: 3 }}>{strategySummary.name}</div>
      <div style={{ fontSize: 12, color: V2.mute, marginBottom: 10 }}>{strategySummary.playerType}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
        {(strategySummary.positionalNeeds || []).map((n) => (
          <span key={n.position} style={{
            fontSize: 10.5, fontWeight: 700, borderRadius: 999, padding: '3px 9px',
            ...solidChip(n.filled ? V2.success : V2.mute),
            textDecoration: n.filled ? 'line-through' : 'none',
          }}>
            {n.position}
          </span>
        ))}
      </div>
      <button onClick={() => navigate(`/league/${leagueId}/draft/strategy`)} style={{ background: 'none', border: 'none', color: V2.blue, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
        View full strategy &rarr;
      </button>
    </V2Card>
  )
}

function CommentsFeed({ comments = [] }) {
  return (
    <V2Card>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: V2.faint, textTransform: 'uppercase', marginBottom: 10 }}>JAI's Comments</div>
      {comments.length === 0 && <div style={{ fontSize: 12.5, color: V2.mute }}>Nothing to flag yet.</div>}
      {comments.map((c, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'flex-start' }}>
          <JAIMascot size={20} state="idle" animate={false} />
          <span style={{ fontSize: 12.5, color: V2.navy, lineHeight: 1.5 }}>{c.text}</span>
        </div>
      ))}
    </V2Card>
  )
}

// ── Manual tracker: search any player ─────────────────────────────────────
// ESPN publishes no picks while a draft is running (confirmed on draft night,
// 9 Oct 2026 — OPS/planning/DRAFT_NIGHT_POSTMORTEM_2026-10-09.md), and Yahoo
// is read the same way, so for those leagues this tracker IS the live draft.
// "Already Selected" on JAI's handful of targets was never enough: ~140
// players go in a real draft and almost none are on that short list. This
// box searches the whole draftable pool instead.

const SEARCH_DEBOUNCE_MS = 150
const SEARCH_RESULT_LIMIT = 8

const trackerBtn = { fontSize: 11.5, fontWeight: 700, borderRadius: 8, padding: '5px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }
const trackerBtnGhost = { ...trackerBtn, color: V2.blue, border: '1px solid rgba(10,31,68,0.2)' }
const trackerBtnSolid = { ...trackerBtn, color: 'white', background: V2.blue, border: '1px solid transparent' }

function PlayerSearch({ leagueId, sport, taken = [], onTaken, onMyPick }) {
  const [pool, setPool] = useState(null) // null = still loading; [] = loaded empty (or failed)
  const [loadError, setLoadError] = useState(null)
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [busy, setBusy] = useState(false)

  // One fetch per tracker mount. The pool is public ranking data the server
  // already caches, and it does not change during a draft.
  useEffect(() => {
    let cancelled = false
    draftBoardApi.getDraftPool(leagueId)
      .then((res) => {
        if (cancelled) return
        setPool((res.data?.players || []).map((p) => ({ ...p, key: normaliseName(p.name) })))
      })
      .catch(() => {
        if (cancelled) return
        setPool([])
        setLoadError("Couldn't load the player list. Reload the page to try again.")
      })
    return () => { cancelled = true }
  }, [leagueId])

  // A short pause before filtering, so a fast typist isn't re-filtering 400
  // names on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [query])

  const needle = normaliseName(debounced)
  const takenKeys = new Set(taken.map(normaliseName))
  const results = needle && pool
    ? pool.filter((p) => !takenKeys.has(p.key) && p.key.includes(needle)).slice(0, SEARCH_RESULT_LIMIT)
    : []

  // Both buttons: record the pick, then clear the box so the next name can be
  // typed straight away. `busy` stops a double-tap recording the pick twice.
  const act = async (fn, name) => {
    if (busy) return
    setBusy(true)
    try {
      await fn(name)
      setQuery('')
      setDebounced('')
    } finally {
      setBusy(false)
    }
  }

  const noteStyle = { fontSize: 12, color: V2.faint, marginTop: 6, padding: '0 2px' }

  return (
    <div data-testid="tracker-search" style={{ marginBottom: 14 }}>
      <input
        type="text" inputMode="search" value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape') setQuery('') }}
        placeholder="Search any player to mark them taken"
        aria-label="Search any player to mark them taken"
        disabled={pool === null}
        autoComplete="off" autoCorrect="off" spellCheck={false}
        style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${V2.hairSoft}`, borderRadius: 10, padding: '10px 14px', fontSize: 14, fontFamily: JAI_FONTS.tight, outline: 'none', background: 'white', color: V2.navy }}
      />
      {pool === null && <div style={noteStyle}>Loading players...</div>}
      {loadError && <div style={{ ...noteStyle, color: V2.warning }}>{loadError}</div>}
      {pool && pool.length === 0 && !loadError && (
        <div style={noteStyle}>No player list is available for this league yet. JAI's targets below can still be marked off.</div>
      )}
      {needle && pool && pool.length > 0 && results.length === 0 && (
        <div style={{ ...noteStyle, color: V2.mute, marginTop: 8 }}>No one left matching "{debounced.trim()}". Players already marked taken are hidden here.</div>
      )}
      {results.map((p) => (
        <div key={`${p.name}|${p.team || ''}`} data-testid="tracker-result" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 4px', borderBottom: `1px solid ${V2.hairSoft}`, flexWrap: 'wrap' }}>
          <span style={{ flex: '1 1 160px', minWidth: 0, fontSize: 13.5, fontWeight: 700, color: V2.navy }}>
            {p.name}
            {p.position && <span style={{ marginLeft: 8 }}><PosChip>{p.position}</PosChip></span>}
            {p.team && <span style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 700, color: teamColor(p.team, sport).ink, background: teamColor(p.team, sport).primary, borderRadius: 999, padding: '1px 7px' }}>{p.team}</span>}
            {p.adp != null && <span style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 600, color: V2.faint, fontFamily: JAI_FONTS.mono }}>ADP {Math.round(p.adp)}</span>}
          </span>
          <span style={{ display: 'flex', gap: 6, marginLeft: 'auto', flexShrink: 0 }}>
            <button onClick={() => act(onTaken, p.name)} disabled={busy} title="Someone else drafted this player" style={{ ...trackerBtnGhost, opacity: busy ? 0.6 : 1 }}>
              Taken
            </button>
            <button onClick={() => act(onMyPick, p.name)} disabled={busy} title="You drafted this player — moves you to your next round" style={{ ...trackerBtnSolid, opacity: busy ? 0.6 : 1 }}>
              My pick
            </button>
          </span>
        </div>
      ))}
    </div>
  )
}

// ── Starter fallback (no live feed) ───────────────────────────────────────

function StarterRecommendations({
  leagueId, sport, myNextPick, recommendations, onMarked, onNext, onBack, onUnmark, onMyPick,
  hasStrategy = true, planCapped = false, roundLimit = null, taken = [], canGoBack = false,
}) {
  const navigate = useNavigate()
  return (
    <V2Card>
      <div style={{ fontSize: 14, fontWeight: 800, color: V2.navy, letterSpacing: '0.03em', textTransform: 'uppercase', marginBottom: 4 }}>
        {myNextPick ? `Your current pick — Round ${myNextPick.round}, Pick ${myNextPick.pickInRound}` : 'Draft complete'}
      </div>
      <div style={{ fontSize: 12, color: V2.mute, marginBottom: 12 }}>
        Mark each pick as it happens in your real draft — search any player below, or tap one of JAI's targets. This slot recomputes instantly.
      </div>
      <PlayerSearch leagueId={leagueId} sport={sport} taken={taken} onTaken={onMarked} onMyPick={onMyPick} />
      {/* Starter's plan covers the first rounds only. Past that line there is
          nothing to recommend, and a pick with a blank list and no reason read
          as "broken". The lock glyph is the allowlisted plan-gated marker. */}
      {planCapped && myNextPick && (
        <Notice tone="info" data-testid="starter-round-cap" style={{ marginTop: 0, marginBottom: 10 }}>
          <div style={{ fontWeight: 800, marginBottom: 4 }}>{STAT_GLYPHS.locked} Starter plans cover rounds 1 to {roundLimit}</div>
          From round {roundLimit + 1} on, take the best player left that fits your roster. Pro unlocks JAI's pick-by-pick plan for every round.
          {!IS_NATIVE_APP && (
            <div style={{ marginTop: 8 }}>
              <a href="/pricing" style={{ color: V2.blue, fontWeight: 700, textDecoration: 'none' }}>See Pro &rarr;</a>
            </div>
          )}
        </Notice>
      )}
      {recommendations.length === 0 && !hasStrategy && myNextPick && (
        <div style={{ fontSize: 12.5, color: V2.mute, lineHeight: 1.55, marginBottom: 6 }}>
          JAI has no plan to follow yet. Lock a strategy to get pick-by-pick recommendations here.
          <div style={{ marginTop: 8 }}>
            <button onClick={() => navigate(`/league/${leagueId}/draft/strategy`)} style={{ background: 'none', border: 'none', padding: 0, color: V2.blue, fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}>
              Set your strategy &rarr;
            </button>
          </div>
        </div>
      )}
      {recommendations.map((t, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 4px', borderBottom: `1px solid ${V2.hairSoft}` }}>
          <span style={{ flex: 1, fontSize: 13.5, fontWeight: 700, color: V2.navy }}>
            {t.name}
            {t.position && <span style={{ marginLeft: 8 }}><PosChip>{t.position}</PosChip></span>}
            {t.team && <span style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 700, color: teamColor(t.team, sport).ink, background: teamColor(t.team, sport).primary, borderRadius: 999, padding: '1px 7px' }}>{t.team}</span>}
          </span>
          <button
            onClick={() => onMarked(t.name)}
            style={{ fontSize: 11.5, fontWeight: 700, color: V2.blue, border: '1px solid rgba(10,31,68,0.2)', borderRadius: 8, padding: '5px 10px', cursor: 'pointer' }}
          >
            Already Selected
          </button>
        </div>
      ))}
      {myNextPick && (
        <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {canGoBack && (
            <button onClick={onBack} style={{ background: 'white', color: V2.navy, border: `1px solid ${V2.hairSoft}`, borderRadius: 9, padding: '9px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
              &larr; Previous pick
            </button>
          )}
          <button onClick={onNext} style={{ background: V2.blue, color: 'white', border: 'none', borderRadius: 9, padding: '9px 18px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
            Next pick &rarr;
          </button>
        </div>
      )}
      {/* Everything marked as taken so far, each with an undo — a misclick on
          "Already Selected" used to be permanent. */}
      {taken.length > 0 && (
        <div data-testid="starter-taken" style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${V2.hairSoft}` }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: V2.faint, textTransform: 'uppercase', marginBottom: 8 }}>
            Marked as taken ({taken.length})
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {taken.map((name) => (
              <span key={name} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 600, color: V2.mute, border: '1px solid rgba(10,31,68,0.16)', borderRadius: 999, padding: '2px 3px 2px 10px' }}>
                {name}
                <button
                  onClick={() => onUnmark(name)} aria-label={`Put ${name} back`} title="Put back"
                  style={{ border: 'none', background: 'none', color: V2.navy, width: 18, height: 18, borderRadius: '50%', cursor: 'pointer', fontSize: 11, lineHeight: 1, padding: 0 }}
                >
                  &times;
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </V2Card>
  )
}

// ── Terminal error state (poll loop gave up) ────────────────────────────────

const errorBtn = { border: 'none', borderRadius: 10, padding: '10px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }
const errorBtnPrimary = { ...errorBtn, background: V2.blue, color: 'white' }
const errorBtnGhost = { ...errorBtn, background: 'white', color: V2.navy, border: `1px solid ${V2.hairSoft}` }

function LiveDraftError({ error, leagueId, onRetry }) {
  const navigate = useNavigate()
  // getLiveDraftState's 400 for unconfirmed league details includes
  // missingFields — that's how we tell "you need to confirm details first"
  // apart from every other permanent failure, without string-matching the
  // message.
  const missingFields = error?.response?.data?.missingFields
  const message = error?.response?.data?.error || error?.message || 'Something went wrong loading your live draft.'

  return (
    <div style={{ textAlign: 'center', padding: '60px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <JAIMascot size={64} state="idle" animate={false} />
      </div>
      <h1 style={{ fontSize: 18, fontWeight: 800, color: V2.navy, margin: '18px 0 8px' }}>Live Draft couldn't load</h1>
      <p style={{ fontSize: 13.5, color: V2.mute, maxWidth: 420, margin: '0 auto 20px' }}>{message}</p>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
        {missingFields?.length > 0 && (
          <button onClick={() => navigate(`/league/${leagueId}/draft/strategy`)} style={errorBtnPrimary}>
            Confirm your league details
          </button>
        )}
        <button onClick={onRetry} style={missingFields?.length > 0 ? errorBtnGhost : errorBtnPrimary}>Try again</button>
        <button onClick={() => navigate(`/league/${leagueId}`)} style={errorBtnGhost}>Go to League Home</button>
      </div>
    </div>
  )
}

// ── ESPN draft-room status ───────────────────────────────────────────────────

function EspnRoomStatus({ bridge, viaExtension }) {
  let text
  if (!bridge.canRelay) {
    text = 'To follow your ESPN draft live, install or update the JAI browser extension, then open your ESPN draft room in another tab.'
  } else if (bridge.needsResync) {
    text = 'JAI missed part of your ESPN draft (this page opened after it began, or a pick was undone). Reload your ESPN draft room tab and JAI will catch up on every pick.'
  } else if (bridge.connected || viaExtension) {
    const n = bridge.pickCount
    text = `Reading your ESPN draft room live${n ? ` (${n} pick${n === 1 ? '' : 's'} so far)` : ''}.`
  } else {
    text = 'JAI extension ready. Open your ESPN draft room in another tab and picks will appear here as they happen.'
  }
  return (
    <div data-testid="espn-room-status" style={{ fontSize: 12.5, color: V2.mute, background: 'white', border: `1px solid ${V2.hairSoft}`, borderRadius: 12, padding: '10px 16px', marginBottom: 14 }}>
      {text}
      {bridge.error && <span style={{ color: V2.warning, marginLeft: 8 }}>ESPN said: {bridge.error}</span>}
    </div>
  )
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function LiveDraft() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { leagues } = useLeagues()
  const league = leagues.find((l) => l.id === Number(id))

  const fetchFn = useCallback((signal) => draftBoardApi.getLiveDraft(id, signal).then((res) => res.data), [id])
  const { data, error, reconnecting, failed, retry, refresh } = useDraftPoll(fetchFn)

  // ESPN publishes no picks on its data API while a draft runs, so the JAI
  // browser extension reads them from the user's own ESPN draft room and this
  // page hands them to the server (which then builds the normal live view).
  const isEspnLeague = (league?.platform || 'espn').toLowerCase() === 'espn'
  const bridge = useEspnDraftBridge({
    espnLeagueId: league?.espnLeagueId,
    enabled: isEspnLeague && !!league,
    onPicks: (picks) => { draftBoardApi.pushLivePicks(id, picks).then(() => refresh()).catch(() => {}) },
  })

  // Starter's manual tracker: after each tap, fetch straight away. The page
  // says "this slot recomputes instantly", but without this it only caught up
  // on the next 10-second poll.
  const handleMarkSelected = async (playerName) => {
    await draftBoardApi.simulateDraftPick(id, { playerName })
    hapticDraftPick()
    refresh()
  }
  const handleNextPick = async () => {
    await draftBoardApi.simulateDraftPick(id, { advance: true })
    hapticDraftPick()
    refresh()
  }
  // "My pick" in the tracker's search box: the player is gone from everyone's
  // board AND it was this user's turn, so move on to their next round in one tap.
  const handleMyPick = async (playerName) => {
    await draftBoardApi.simulateDraftPick(id, { playerName, advance: true })
    hapticDraftPick()
    refresh()
  }

  const handleUnmark = async (playerName) => {
    await draftBoardApi.simulateDraftPick(id, { unmark: playerName })
    refresh()
  }
  const handleBack = async () => {
    await draftBoardApi.simulateDraftPick(id, { back: true })
    refresh()
  }

  const wide = useMinWidth(820)

  // Funnel events: which draft-room mode the user lands in, and that they got to the end.
  const trackedOpen = useRef(false)
  const trackedDone = useRef(false)
  useEffect(() => {
    if (!data) return
    if (!trackedOpen.current) {
      trackedOpen.current = true
      trackEvent('draft_live_opened', { mode: data.liveFeed === false ? 'manual' : 'live', status: data.status, has_strategy: !!data.strategySummary })
    }
    if (data.status === 'complete' && !trackedDone.current) {
      trackedDone.current = true
      trackEvent('draft_live_completed', { mode: data.liveFeed === false ? 'manual' : 'live' })
    }
  }, [data])

  const sport = (league?.sport || 'nba').toLowerCase()
  // ESPN's read API publishes no picks while a draft is running (confirmed on
  // draft night, 9 Oct 2026), so a stalled ESPN feed is the expected case, not
  // a glitch — the banner says so plainly instead of promising live tracking
  // "as soon as picks appear". The 'espn' default mirrors the League model's.
  const isEspn = (league?.platform || 'espn').toLowerCase() === 'espn'
  // Starter's "not_started" just means no picks have been manually marked
  // off yet in the simulated tracker — that tracker IS the page for them,
  // so it still needs to render. Only the real platform feed gets the
  // "come back once your draft begins" empty state.
  const showNotStartedEmptyState = data?.status === 'not_started' && data.liveFeed !== false

  return (
    <AppShell>
      <div style={{ padding: wide ? '30px 40px' : '20px 16px', fontFamily: JAI_FONTS.tight }}>
        <div style={{ maxWidth: 1040, margin: '0 auto' }}>
          <ToolHeader
            leagueName={league?.name}
            title="Live Draft"
            subtitle={[league?.teamCount ? `${league.teamCount} teams` : null, league?.draftType, data?.leagueType && data.leagueType !== 'redraft' ? `${data.leagueType} league` : null, league?.teamName].filter(Boolean).join(' · ') || undefined}
            back={{ label: 'Go to League Home Screen', to: `/league/${id}` }}
            backRight={{ label: 'Go to Draft Room', to: `/league/${id}/draft` }}
          />

          {failed && <LiveDraftError error={error} leagueId={id} onRetry={retry} />}

          {!failed && (
            <>
              {reconnecting && (
                <Notice tone="warning" style={{ marginTop: 0 }}>Reconnecting…</Notice>
              )}

              {!data && !error && (
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <JAIMascot size={56} state="thinking" animate />
                  </div>
                  <div style={{ fontSize: 12.5, color: V2.faint, marginTop: 10 }}>Loading live draft…</div>
                </div>
              )}

              {data && data.status === 'complete' && (
                <MascotDialog
                  state="happy" size={200}
                  headline="Your draft is complete"
                  body="See how every pick graded against your plan."
                  actions={[
                    { label: 'Go to Draft Review', primary: true, onClick: () => navigate(`/league/${id}/draft/review`) },
                    // Starter's tracker is manual, so a stray "Next pick" can end it early.
                    ...(data.liveFeed === false ? [{ label: 'Undo last pick', onClick: handleBack }] : []),
                  ]}
                  onClose={() => {}} closable={false}
                />
              )}

              {/* Nobody's been picked yet — a ticker/recommendations view here
                  would have nothing real to show. This used to fall through
                  to the in-progress layout and render ESPN's unfilled draft
                  slots as if they were real picks (2026-08-12). */}
              {data && showNotStartedEmptyState && (
                <MascotDialog
                  state="idle" size={160}
                  headline="Your draft hasn't started yet"
                  body={
                    league?.draftDate
                      ? `Scheduled for ${new Date(league.draftDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} — check back once it begins.`
                      : "Your league hasn't scheduled a draft date yet — check back once it's set."
                  }
                  actions={[
                    data.strategySummary
                      ? { label: 'View your strategy', primary: true, onClick: () => navigate(`/league/${id}/draft/strategy`) }
                      : { label: 'Set your strategy', primary: true, onClick: () => navigate(`/league/${id}/draft/strategy`) },
                    { label: 'Go to League Home', onClick: () => navigate(`/league/${id}`) },
                  ]}
                  onClose={() => {}} closable={false}
                />
              )}

              {data && data.status !== 'complete' && !showNotStartedEmptyState && (
                <>
                  {isEspn && (data.liveFeed !== false ? data.viaExtension || bridge.canRelay : data.feedStalled) && (
                    <EspnRoomStatus bridge={bridge} viaExtension={!!data.viaExtension} />
                  )}
                  {data.liveFeed === false ? (
                    <div style={{ background: 'white', border: `1px solid ${V2.hairSoft}`, borderRadius: 12, padding: '12px 16px', fontSize: 12.5, color: V2.mute, marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                      <span>{data.feedStalled
                        ? (isEspn
                          ? "ESPN doesn't share picks while your draft is running. Mark each pick here as it happens and JAI keeps your recommendations current."
                          : "Your league's draft feed isn't showing picks yet, so mark each pick yourself below. JAI switches back to live tracking as soon as picks appear.")
                        : 'Pro connects to your live draft and tracks every pick for you.'}</span>
                      {!data.feedStalled && !IS_NATIVE_APP && (
                        <a href="/pricing" style={{ color: V2.blue, fontWeight: 700, textDecoration: 'none' }}>Upgrade</a>
                      )}
                    </div>
                  ) : (
                    <DraftTicker picks={data.picks} sport={sport} />
                  )}

                  <div style={{ display: 'flex', flexDirection: wide ? 'row' : 'column', gap: 20, alignItems: wide ? 'flex-start' : 'stretch' }}>
                    <div style={{ flex: 2, minWidth: 0 }}>
                      {data.liveFeed === false ? (
                        <StarterRecommendations
                          leagueId={id} sport={sport}
                          myNextPick={data.myNextPick} recommendations={data.recommendations}
                          hasStrategy={!!data.strategySummary}
                          onMarked={handleMarkSelected} onNext={handleNextPick}
                          onBack={handleBack} onUnmark={handleUnmark} onMyPick={handleMyPick}
                          planCapped={!!data.planCapped} roundLimit={data.roundLimit}
                          taken={data.taken || []} canGoBack={(data.simulatedRound || 1) > 1}
                        />
                      ) : (
                        <RecommendationsPanel
                          leagueId={id} sport={sport}
                          myNextPick={data.myNextPick} recommendations={data.recommendations}
                          hasStrategy={!!data.strategySummary}
                          onTheClock={data.onTheClock}
                        />
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
                      <StrategyOverviewCard leagueId={id} strategySummary={data.strategySummary} />
                      <CommentsFeed comments={data.comments} />
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </AppShell>
  )
}
